import queryString from 'query-string';

import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { ApiOrgMini } from 'api/org/types/ApiOrgMini';
import { ApiOrgSize } from 'api/org/types/ApiOrgSize';
import { ApiSavedSearch } from 'api/savedSearch/types/ApiSavedSearch';
import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { ApiIntakeIntents } from 'api/types/ApiIntakeIntents';
import { ApiListingMiniExtra } from 'api/types/ApiListingMini';
import { UserDashboardSavedListingItem } from 'modules/userDashboard/types/UserDashboardSavedItem';
import { BackgroundTask, waitTaskCompleted } from 'utils/asyncBackendTask';
import { del, getJson, postJson, putJson } from 'utils/http';

import { ApiPreferredListingType } from './types/ApiPreferredListingType';

export type Image = {
  filename: string;
  handle: string;
  mimetype: string;
  size: number;
  source: string;
};
export type UserInfo = {
  id: string;
  created: number;
  updated: number;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  emailVerified: boolean;
  hasPassword: boolean;
  locale: string;
  hasPaymentInfo: boolean;
  locationText: string;
  stateCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  timezone: string;
  profile:
    | {
        description: string;
        links: Array<string>;
        image: Image | null | undefined;
      }
    | null
    | undefined;
  isLastOwnerOfOrgs: Array<{
    id: string;
    name: string;
    hasOutstandingInvoices: boolean;
    credits: number;
  }>;
  hasEvents: boolean;
  canExportData: boolean;
};
export type SavedSearch = ApiSavedSearch;
export type ListingApplication = {
  created: string;
  id: string;
  listing: ApiListingMiniExtra;
  org: ApiOrgMini;
};
export type GeneralUserInfo = {
  user: UserInfo;
  savedSearches: Array<SavedSearch>;
  authProviders: Array<string>;
};
export type NotificationPreferences = {
  updatedByUser?: number | null | undefined;
  receiveEmails: boolean | null | undefined;
  receiveEmailsExplicitlyChosen: boolean | null | undefined;
};
export type PaymentMethod = {
  id: string;
  brand: string;
  last4: string;
  name: string;
  type: string;
  onPendingPayment?: boolean;
};
export type SavedListingData<T> = {
  savedListings: T[];
  page: number;
  count: number;
  pageCount: number;
};
export function getNotificationPreferences(): Promise<NotificationPreferences> {
  return getJson(`/data/userdashboard/notification-preferences`).then(
    // @ts-expect-error TS(2345): Argument of type '({ notificationPreferences }: { ... Remove this comment to see the full error message
    ({ notificationPreferences }) => notificationPreferences,
  );
}
export function updateNotificationPreferences(
  preferences: NotificationPreferences,
): Promise<NotificationPreferences> {
  return putJson(
    `/data/userdashboard/notification-preferences`,
    preferences,
    // @ts-expect-error TS(2345): Argument of type '({ notificationPreferences }: { ... Remove this comment to see the full error message
  ).then(({ notificationPreferences }) => notificationPreferences);
}
export function getPaymentMethods(): Promise<Array<PaymentMethod>> {
  // @ts-expect-error TS(2345): Argument of type '({ task }: { task: any; }) => Pr... Remove this comment to see the full error message
  return getJson('/data/payment-methods').then(({ task }) =>
    waitTaskCompleted(task).then((result) => {
      // @ts-expect-error TS(2339): Property 'success' does not exist on type 'unknown... Remove this comment to see the full error message
      const { success, sources } = result;

      if (!success) {
        throw new Error(`Error fetching Stripe payment sources`);
      }

      return sources;
    }),
  );
}
export type AddPaymentMethodResult = {
  success: boolean;
  source?: PaymentMethod;
  errorHuman?: string;
  error:
    | {
        msg: string;
        stripeCode: string;
      }
    | null
    | undefined;
};
export function addPaymentMethod(
  token: string,
): Promise<BackgroundTask<AddPaymentMethodResult>> {
  return postJson(`/data/userdashboard/payment-methods`, {
    token,
    // @ts-expect-error TS(2345): Argument of type '({ task }: { task: any; }) => an... Remove this comment to see the full error message
  }).then(({ task }) => task);
}
export function removePaymentMethod(methodId: string): Promise<boolean> {
  return del(`/data/userdashboard/payment-methods/${methodId}`).then(
    // @ts-expect-error TS(2345): Argument of type '({ task }: { task: any; }) => Pr... Remove this comment to see the full error message
    ({ task }) =>
      waitTaskCompleted(task).then((result) => {
        // @ts-expect-error TS(2339): Property 'success' does not exist on type 'unknown... Remove this comment to see the full error message
        const { success } = result;
        return success;
      }),
  );
}
export function fetchSavedListings<T extends UserDashboardSavedListingItem>(
  listingType: string,
  page: number,
): Promise<SavedListingData<T>> {
  return getJson(
    `/data/userdashboard/saved-listings?listingType=${listingType}&page=${page}`,
  );
}

export function fetchListingApplications(params: {
  page: number | null | undefined;
}) {
  const qs = queryString.stringify(params);
  return getJson(`/data/listing-applications?${qs}`).then(
    // @ts-expect-error TS(2345): Argument of type '({ listingApplications }: { list... Remove this comment to see the full error message
    ({ listingApplications }) => listingApplications,
  );
}

export type ProfileInfo = {
  description: string;
  links: Array<string>;
  image: ApiFilestackImageData | null | undefined;
};

export type GeneralSettings = {
  firstName: string;
  lastName: string;
  locale: UserLocale;
  email: string;
  location: SearchLocation;
  optedInResearch: boolean;
  profile?: ProfileInfo;
  authProviders: Array<'google_oauth2' | 'facebook' | 'apple'>;
  canExportData: boolean;
  redirectTo?: string;
  orgSize: ApiOrgSize;
  preferredListingTypes: ApiPreferredListingType[];
  intakeIntents: ApiIntakeIntents[];
  intakeIntentSomethingElse: string;
};

export function fetchGeneralSettings(): Promise<GeneralSettings> {
  return getJson('/data/userdashboard/general-settings').then(
    // @ts-expect-error TS(2345): Argument of type '({ generalSettings }: { generalS... Remove this comment to see the full error message
    ({ generalSettings }) => generalSettings,
  );
}

export function updateSettings(
  settings: Partial<GeneralSettings>,
): Promise<GeneralSettings> {
  return putJson('/data/userdashboard/general-settings', settings).then(
    // @ts-expect-error TS(2345): Argument of type '({ generalSettings }: { generalS... Remove this comment to see the full error message
    ({ generalSettings }) => generalSettings,
  );
}

export function updateProfile(
  profileInfo: ProfileInfo,
): Promise<GeneralSettings> {
  return putJson('/data/userdashboard/profile', profileInfo).then(
    // @ts-expect-error TS(2345): Argument of type '({ generalSettings }: { generalS... Remove this comment to see the full error message
    ({ generalSettings }) => generalSettings,
  );
}

export function updateMissingInfo(
  userInfo: Partial<GeneralSettings>,
): Promise<GeneralSettings> {
  return postJson('/data/userdashboard/missing-info', userInfo).then(
    // @ts-expect-error TS(2345): Argument of type '({ generalSettings }: { generalS... Remove this comment to see the full error message
    ({ generalSettings }) => generalSettings,
  );
}
